import { motion } from 'framer-motion';

interface NetworkQualityIndicatorProps {
  /** Network quality level 0-5 (0 = unknown) */
  level: number;
  /** Optional CSS class */
  className?: string;
}

/**
 * Quality level to bar color mapping.
 * - 4-5: green (Excellent/Good)
 * - 3: amber (Fair)
 * - 1-2: red (Poor)
 * - 0: gray (Unknown)
 */
function getBarColor(level: number, barIndex: number): string {
  if (level === 0) return 'bg-gray-400';
  if (barIndex >= level) return 'bg-gray-300';
  if (level >= 4) return 'bg-green-500';
  if (level === 3) return 'bg-amber-500';
  return 'bg-red-500';
}

function getQualityLabel(level: number): string {
  if (level === 0) return 'Connection: Unknown';
  if (level >= 5) return 'Connection: Excellent';
  if (level >= 4) return 'Connection: Good';
  if (level >= 3) return 'Connection: Fair';
  if (level >= 2) return 'Connection: Poor';
  return 'Connection: Very Poor';
}

/**
 * 5-bar network quality indicator (like mobile signal).
 *
 * Bars fill based on level (0-5).
 * Color: green (4-5), amber (3), red (1-2), gray (0/unknown).
 * Subtle pulse animation when quality < 3.
 */
export function NetworkQualityIndicator({ level, className = '' }: NetworkQualityIndicatorProps) {
  const isLow = level > 0 && level < 3;
  const barHeights = [4, 7, 10, 13, 16]; // px heights for 5 bars

  return (
    <motion.div
      className={`flex items-end gap-[2px] ${className}`}
      title={getQualityLabel(level)}
      data-testid="network-quality-indicator"
      data-level={level}
      animate={isLow ? { opacity: [1, 0.6, 1] } : { opacity: 1 }}
      transition={isLow ? { repeat: Infinity, duration: 1.5 } : {}}
    >
      {barHeights.map((height, index) => (
        <div
          key={index}
          className={`w-[3px] rounded-full transition-colors duration-300 ${getBarColor(level, index)}`}
          style={{ height: `${height}px` }}
          data-testid={`nq-bar-${index}`}
        />
      ))}
    </motion.div>
  );
}
